import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, Brain, GitCommitVertical, Sun, ClipboardList, Scale, CalendarRange,
  DollarSign, FileText, Check, MessagesSquare, ShieldCheck, Lock, Layers,
  AlertTriangle, Newspaper,
} from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Wordmark, Footer } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from "@/components/ui/accordion";
import shotToday from "@/assets/proof/today.png";
import shotCommand from "@/assets/proof/command.png";
import shotReview from "@/assets/proof/review.png";

const BG =
  "https://images.unsplash.com/photo-1709625862266-014ef072fd93?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMGRhcmslMjBnZW9tZXRyeXxlbnwwfHx8fDE3ODc4ODExODJ8MA&ixlib=rb-4.1.0&q=85";

const LOOP = ["Situation", "Priority", "Commitment", "Follow-through", "Outcome", "Learning"];

const problems = [
  "Context scattered across your head, chats, docs and spreadsheets",
  "Competing priorities with no honest ranking",
  "The hard decision quietly avoided for another week",
  "Commitments made, then lost before they close",
  "Financial visibility that's always a little out of date",
  "No operating cadence — every day starts from zero",
];

const capabilities = [
  { icon: Brain, title: "Natural business memory", body: "Sarge records how your company works — offer, customer, model, constraints — and lets you correct or delete anything. Nothing is invented." },
  { icon: MessagesSquare, title: "Grounded Ask Sarge", body: "Ask anything; answers are drawn from your stored operating record, with facts, inference and unknowns kept separate." },
  { icon: Sun, title: "Daily Command Cycle", body: "Each day: the situation, the single next move that matters most, and why it outranks the alternatives." },
  { icon: GitCommitVertical, title: "Tracked commitments", body: "Accepted moves become commitments with owners and due dates. Overdue items resurface until resolved." },
  { icon: ClipboardList, title: "Command Board", body: "Priorities, projects, commitments, risks and decisions in one grounded operating picture." },
  { icon: Scale, title: "Decision Journal + outcomes", body: "Capture context, alternatives and expected outcome. Sarge resurfaces decisions to judge the actual result and the lesson." },
  { icon: CalendarRange, title: "Weekly Operating Review", body: "Promised vs delivered, decisions due, risks moving, cash changes — plus an immutable history you can revisit." },
  { icon: DollarSign, title: "Financial Pulse", body: "Cash, burn and runway computed deterministically — never guessed — with an always-visible as-of date and stale-data flags." },
  { icon: FileText, title: "File analysis & documents", body: "Analyze an uploaded file or draft an operating document, grounded in your records." },
  { icon: Layers, title: "Usage & cost controls", body: "A clear monthly model-request allowance with an 80% warning and a truthful stop at the limit." },
  { icon: Lock, title: "Privacy on your terms", body: "Request a data export or account deletion at any time — actioned manually, never automatically." },
  { icon: ShieldCheck, title: "Read-only Google (not configured)", body: "Optional Calendar/Gmail read-only signals are built to be enabled after configuration — never live unless truly live." },
];

const differentiators = [
  ["Not a chat that forgets", "General assistants reset into a fresh conversation. Sarge keeps the durable record of what you knew, decided, committed to and learned."],
  ["Not a task manager", "Task tools schedule work but never make an operating judgment. Sarge names the one move that matters and why."],
  ["Not an assistant swarm", "You don't need more content or more bots. Sarge is one coherent COO, not a novelty crowd."],
  ["Not detached coaching", "Advice without your company's operating history is generic. Sarge reasons from your actual records."],
];

const trust = [
  "You are the final authority — Sarge proposes, you decide.",
  "Facts, inference and unknowns are always kept distinct.",
  "Proposed board actions require your explicit confirmation.",
  "Sarge never claims it took an external action it cannot take.",
  "Every WarRoom is tenant-isolated from every other.",
  "Founding Beta access and usage boundaries are stated plainly.",
];

const faqs = [
  ["Is Sarge a chatbot?", "No. Sarge is a continuing operating system for founders — an AI COO. Chat is one surface, but the core is a daily command cycle, a command board, a decision journal, weekly reviews and a financial pulse that persist over time."],
  ["Does Sarge make decisions for me?", "No. Sarge recommends one next move and explains why, but you accept, modify or reject it. Nothing becomes a commitment on the board without your explicit confirmation."],
  ["What does Sarge remember?", "How your business works — company, owner, objectives, revenue, constraints — plus your decisions, commitments and the outcomes you record. It's your durable operating record, grounded and correctable."],
  ["Can I correct or delete information?", "Yes. You can correct anything in memory, and you can request a full data export or account deletion at any time. During the beta these requests are actioned manually — nothing is deleted automatically."],
  ["Does Sarge connect to my email or calendar?", "Not in this release. Read-only Google Calendar and Gmail connectors are built but shown as 'Not configured' and cannot be connected until credentials are provisioned. Sarge never sends email, replies, or changes anything in your accounts."],
  ["How is financial runway calculated?", "Deterministically, server-side, from the figures you enter — never estimated by the model. When required inputs are missing or net burn is non-positive, Sarge says it cannot calculate rather than guessing, and always shows the as-of date."],
  ["Is my WarRoom isolated?", "Yes. Each workspace is fully tenant-isolated. Your data is resolved from your membership on the server and is never exposed to another WarRoom."],
  ["What happens at the usage limit?", "Each WarRoom includes 500 model requests per month. You'll see a warning at 80%, and at the limit Sarge stops truthfully and asks you to contact your operator rather than failing silently."],
  ["What does Founding Beta mean?", "The first 10 WarRooms. You purchase instantly through secure Stripe checkout — $149/month or $1,490/year paid upfront — and your account and WarRoom are created automatically. Your initial pricing is locked for the first 12-month term."],
  ["What's your refund policy?", "14-day money-back guarantee on your first payment. After that, cancel anytime and your access continues through the end of your paid billing term. No prorated refunds except where required by law. Refunds under the guarantee are handled by our team, not decided automatically by Stripe."],
  ["How do I get started?", "Choose Monthly or Annual and complete secure checkout on Stripe. Your WarRoom is created automatically and you'll get a “Set your password” email to sign in — or set your password right on the confirmation page. When all 10 Founding WarRooms are taken, the button becomes a waitlist."],
];

const REVENUE = ["", "Pre-revenue", "< $10k / mo", "$10k–$50k / mo", "$50k–$250k / mo", "$250k+ / mo"];
const TEAM = ["", "Just me", "2–5", "6–15", "16–50", "50+"];

function BetaForm({ onDone }) {
  const [form, setForm] = useState({
    name: "", email: "", company: "", website: "", revenue_range: "", team_size: "",
    operating_constraint: "", plan_interest: "monthly", agree_contact: false,
  });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.agree_contact) { toast.error("Please agree to be contacted."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/beta/request", form);
      toast.success(data.message || "Request received.");
      onDone?.();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  const field = "mt-1.5 bg-wr-bg border-wr-border rounded-none focus-visible:ring-0";
  const sel = "mt-1.5 w-full bg-wr-bg border border-wr-border text-white text-sm px-3 py-2 rounded-none focus:outline-none";
  return (
    <form onSubmit={submit} className="space-y-3.5 max-h-[70vh] overflow-y-auto pr-1" data-testid="beta-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
        <div><label className="wr-label">Full name *</label>
          <Input required data-testid="beta-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={field} /></div>
        <div><label className="wr-label">Work email *</label>
          <Input required type="email" data-testid="beta-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={field} /></div>
        <div><label className="wr-label">Company *</label>
          <Input required data-testid="beta-company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className={field} /></div>
        <div><label className="wr-label">Website <span className="text-wr-muted">(optional)</span></label>
          <Input data-testid="beta-website" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className={field} /></div>
        <div><label className="wr-label">Current revenue <span className="text-wr-muted">(optional)</span></label>
          <select data-testid="beta-revenue" value={form.revenue_range} onChange={(e) => setForm({ ...form, revenue_range: e.target.value })} className={sel}>
            {REVENUE.map((r) => <option key={r} value={r}>{r || "Prefer not to say"}</option>)}</select></div>
        <div><label className="wr-label">Team size</label>
          <select data-testid="beta-team" value={form.team_size} onChange={(e) => setForm({ ...form, team_size: e.target.value })} className={sel}>
            {TEAM.map((t) => <option key={t} value={t}>{t || "Select…"}</option>)}</select></div>
      </div>
      <div><label className="wr-label">Primary operating constraint right now</label>
        <Textarea data-testid="beta-constraint" value={form.operating_constraint} onChange={(e) => setForm({ ...form, operating_constraint: e.target.value })} placeholder="What are you trying to operate through?" className={`${field} min-h-[80px]`} /></div>
      <div><label className="wr-label">Plan interest</label>
        <div className="mt-1.5 grid grid-cols-3 gap-2" data-testid="beta-plan">
          {[["monthly", "$149 / mo"], ["annual", "$1,490 / yr"], ["undecided", "Undecided"]].map(([v, label]) => (
            <button type="button" key={v} data-testid={`beta-plan-${v}`} onClick={() => setForm({ ...form, plan_interest: v })}
              className={`wr-mono text-[0.66rem] uppercase tracking-wider py-2 border ${form.plan_interest === v ? "border-wr-red text-white bg-wr-redMuted" : "border-wr-border text-wr-sub hover:text-white"}`}>{label}</button>
          ))}
        </div></div>
      <label className="flex items-start gap-2.5 cursor-pointer" data-testid="beta-agree-label">
        <Checkbox checked={form.agree_contact} onCheckedChange={(v) => setForm({ ...form, agree_contact: !!v })} data-testid="beta-agree"
          className="mt-0.5 rounded-none border-wr-border data-[state=checked]:bg-wr-red data-[state=checked]:border-wr-red" />
        <span className="text-xs text-wr-sub">I agree to be contacted about Founding Beta availability. My details stay private to WarRoom Solutions.</span>
      </label>
      <Button type="submit" disabled={busy} data-testid="beta-submit" className="w-full rounded-none bg-wr-red hover:bg-wr-redHover text-white wr-mono uppercase tracking-wider h-11">
        {busy ? "Submitting…" : "Join the Waitlist"}
      </Button>
      <p className="text-xs text-wr-muted">We'll only email you about Founding Beta availability. Joining the waitlist creates no account and no charge.</p>
    </form>
  );
}

function Section({ id, kicker, title, children, className = "" }) {
  return (
    <section id={id} className={`max-w-7xl mx-auto w-full px-4 sm:px-8 py-16 border-t border-wr-border ${className}`} data-testid={`section-${id}`}>
      {kicker && <p className="wr-label mb-3">{kicker}</p>}
      {title && <h2 className="wr-heading font-bold text-white text-2xl sm:text-3xl max-w-3xl leading-tight">{title}</h2>}
      <div className="mt-8">{children}</div>
    </section>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [billing, setBilling] = useState(null);
  const [buyPlan, setBuyPlan] = useState(null);
  const [buyEmail, setBuyEmail] = useState("");
  const [buyBusy, setBuyBusy] = useState(false);

  useEffect(() => {
    api.get("/billing/config").then(({ data }) => setBilling(data)).catch(() => setBilling({ configured: false }));
  }, []);

  const canBuy = billing?.configured && !billing?.capacity?.sold_out;
  const soldOut = billing?.capacity?.sold_out;

  const startCheckout = async (e) => {
    e.preventDefault();
    if (!buyEmail) return;
    setBuyBusy(true);
    try {
      const { data } = await api.post("/billing/checkout", {
        lookup_key: buyPlan, email: buyEmail, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(apiError(err));
      setBuyBusy(false);
    }
  };

  const BuyButton = ({ plan, testid, children }) => (
    <Button data-testid={testid} onClick={() => { setBuyPlan(plan); setBuyEmail(""); }}
      className="rounded-full bg-wr-red hover:bg-wr-redHover text-white wr-mono uppercase tracking-wider px-6 h-11">
      {children}
    </Button>
  );

  const WALKTHROUGH_MAILTO =
    "mailto:mm@titanhavengroup.com?subject=" +
    encodeURIComponent("Book a 15-minute operating walkthrough — WarRoom AI · Sarge") +
    "&body=" +
    encodeURIComponent(
      "Hi WarRoom Solutions team,\n\nI'd like to book a 15-minute operating walkthrough of Sarge.\n\nName:\nCompany:\nBest times (with timezone):\n\nThanks,"
    );

  const WalkthroughButton = ({ testid, size }) => (
    <Button asChild variant="outline"
      className={`rounded-full border-wr-border bg-transparent text-white hover:bg-wr-surface2 hover:text-white wr-mono uppercase tracking-wider ${size === "lg" ? "px-6 h-12" : "text-xs px-4 h-11"}`}>
      <a href={WALKTHROUGH_MAILTO} data-testid={testid}>
        <CalendarRange size={size === "lg" ? 16 : 14} className="mr-2" /> Book a 15-minute operating walkthrough
      </a>
    </Button>
  );

  useEffect(() => {
    const link = document.querySelector("link[rel=canonical]") || (() => {
      const l = document.createElement("link"); l.rel = "canonical"; document.head.appendChild(l); return l;
    })();
    link.href = "https://sarge.titanhavengroup.com/";
  }, []);

  const scrollToPricing = () =>
    document.getElementById("founding-beta")?.scrollIntoView({ behavior: "smooth" });

  const RequestButton = ({ testid, size }) => (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid={testid} className={`rounded-full bg-wr-red hover:bg-wr-redHover text-white wr-mono uppercase tracking-wider ${size === "lg" ? "px-6 h-12" : "text-xs px-4"}`}>
          Join the Waitlist {size === "lg" && <ArrowRight size={16} className="ml-2" />}
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-wr-surface border-wr-border rounded-none max-w-lg">
        <DialogHeader>
          <DialogTitle className="wr-heading text-white">Join the Founding Waitlist</DialogTitle>
          <DialogDescription className="text-wr-sub">The first 10 Founding WarRooms are being claimed. Join the waitlist and we'll email you the moment a seat opens.</DialogDescription>
        </DialogHeader>
        <BetaForm onDone={() => setOpen(false)} />
      </DialogContent>
    </Dialog>
  );

  // Primary CTA is self-serve checkout. It falls back to the waitlist only when the 10 Founding
  // WarRooms are sold out or payments are temporarily unavailable.
  const PrimaryCTA = ({ testid, size }) =>
    canBuy ? (
      <Button data-testid={testid} onClick={scrollToPricing}
        className={`rounded-full bg-wr-red hover:bg-wr-redHover text-white wr-mono uppercase tracking-wider ${size === "lg" ? "px-6 h-12" : "text-xs px-4"}`}>
        Start Founding Beta {size === "lg" && <ArrowRight size={16} className="ml-2" />}
      </Button>
    ) : (
      <RequestButton testid={testid} size={size} />
    );

  return (
    <div className="min-h-screen bg-wr-bg flex flex-col overflow-x-hidden">
      <header className="border-b border-wr-border bg-black/80 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between gap-2">
          <Wordmark size="md" showTagline />
          <div className="flex items-center gap-2 sm:gap-3">
            <Button variant="ghost" data-testid="landing-signin" onClick={() => navigate("/login")}
              className="rounded-none text-wr-sub hover:text-white hover:bg-wr-surface2 wr-mono uppercase tracking-wider text-xs">Sign In</Button>
            <PrimaryCTA testid="landing-request-beta" />
          </div>
        </div>
      </header>
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-wr-border">
        <div className="absolute inset-0 opacity-40" style={{ backgroundImage: `url(${BG})`, backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/85 to-wr-bg" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-8 py-20 sm:py-28">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-3xl">
            <p className="wr-mono text-[0.62rem] uppercase tracking-[0.25em] text-wr-muted" data-testid="hero-hierarchy">WarRoom AI · Sarge · A WarRoom Solutions Product</p>
            <span className="inline-flex items-center gap-2 wr-mono text-[0.68rem] uppercase tracking-wider text-wr-red border border-wr-red/40 bg-wr-redMuted px-3 py-1.5 mt-4" data-testid="founding-badge">
              <span className="h-1.5 w-1.5 rounded-full bg-wr-red animate-pulse" /> Founding Beta Now Open
            </span>
            <h1 className="wr-heading font-black tracking-tight text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mt-5" data-testid="hero-headline">
              Sarge knows your business, names what needs attention now, and holds the line on follow-through.
            </h1>
            <p className="wr-heading text-xl sm:text-2xl text-wr-sub mt-5 font-semibold">Your WarRoom Confidant — the AI COO for Founders.</p>
            <p className="text-base sm:text-lg text-zinc-300 mt-5 max-w-2xl leading-relaxed">
              Sarge learns how your company works, identifies what needs attention now, turns the decision into
              accountable action, and follows it through — day after day. You keep final authority; Sarge proposes, you decide.
            </p>
            <div className="flex flex-col sm:flex-row flex-wrap gap-3 mt-9">
              <PrimaryCTA testid="hero-request-beta" size="lg" />
              <WalkthroughButton testid="hero-walkthrough" size="lg" />
              <Button variant="outline" data-testid="hero-signin" onClick={() => navigate("/login")}
                className="rounded-full border-wr-border bg-transparent text-white hover:bg-wr-surface2 hover:text-white wr-mono uppercase tracking-wider px-6 h-12">Sign In</Button>
            </div>
            <p className="wr-mono text-[0.7rem] text-wr-muted mt-5">$149/month · $1,490/year paid upfront · Limited to the first 10 WarRooms</p>
          </motion.div>
        </div>
      </section>

      {/* PROBLEM */}
      <Section id="problem" kicker="The founder operating problem" title="You don't need another idea or another writing tool. You need to operate.">
        <p className="text-base text-wr-sub max-w-3xl leading-relaxed">
          Most founders aren't short on ideas. They're carrying the whole company in their head — and it leaks.
          The real drag is operational:
        </p>
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-wr-border border border-wr-border">
          {problems.map((p, i) => (
            <div key={i} className="bg-wr-surface p-5 flex items-start gap-3" data-testid={`problem-${i}`}>
              <AlertTriangle size={16} className="text-wr-red mt-0.5 shrink-0" />
              <p className="text-sm text-wr-sub leading-relaxed">{p}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* WHAT SARGE IS */}
      <Section id="what" kicker="What Sarge is" title="A continuing founder operating system — one coherent AI COO, not a chatbot and not a swarm of assistants.">
        <p className="text-base text-wr-sub max-w-3xl leading-relaxed">
          Sarge runs a closed operating loop around you and keeps it turning. Every recommendation is grounded in
          your own records, and every step is yours to accept, change or reject.
        </p>
        <div className="mt-8 flex flex-wrap items-center gap-2" data-testid="loop">
          {LOOP.map((step, i) => (
            <div key={step} className="flex items-center gap-2">
              <span className="wr-mono text-[0.68rem] uppercase tracking-wider text-white border border-wr-red/40 bg-wr-redMuted px-3 py-2">{step}</span>
              {i < LOOP.length - 1 && <ArrowRight size={14} className="text-wr-muted" />}
            </div>
          ))}
        </div>
      </Section>

      {/* FIRST DAY */}
      <Section id="first-day" kicker="How the first day works" title="Sarge onboards like a COO, not a form.">
        <ol className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-4xl">
          {[
            ["Conversational intake", "Sarge interviews you about the company, the owner, the objective, revenue and your real constraints — in plain conversation, not a wall of fields."],
            ["Natural memory forms", "What matters is recorded automatically as your operating memory — no separate approval step — and always stays correctable."],
            ["Initial operating read", "Sarge gives you an honest first read of the situation — what's strong, what's fragile, what's unknown."],
            ["Your first Command Cycle", "You get the first daily move to accept, modify or reject. From day one, nothing happens without your say-so."],
          ].map(([t, b], i) => (
            <li key={t} className="border-l-2 border-wr-red/40 pl-4" data-testid={`firstday-${i}`}>
              <p className="wr-mono text-[0.62rem] text-wr-muted">STEP {i + 1}</p>
              <h3 className="text-white font-semibold mt-1">{t}</h3>
              <p className="text-sm text-wr-sub mt-1 leading-relaxed">{b}</p>
            </li>
          ))}
        </ol>
      </Section>

      {/* DAILY EXPERIENCE */}
      <Section id="daily" kicker="The daily experience" title="Seven surfaces that work as one operating system.">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-wr-border border border-wr-border">
          {[
            ["Today", "The situation, one recommended move, and accept / modify / reject. End the day by recording the result."],
            ["Command", "Your board of priorities, projects, commitments and risks — the single source of operating truth."],
            ["Decisions", "The journal of what you decided and why, with a revisit queue when it's time to judge the outcome."],
            ["Review", "The weekly operating review: promised vs delivered, decisions due, risks and cash — with history."],
            ["Financial", "Cash, burn and runway with an as-of date and stale-data warnings. Update it and Sarge stays honest."],
            ["Sarge", "Grounded conversation and the file/document tools, always drawing on your operating record."],
            ["What Sarge Knows", "Your memory, transparent and correctable — you can add, fix or remove at any time."],
          ].map(([t, b], i) => (
            <div key={t} className="bg-wr-surface p-5" data-testid={`daily-${i}`}>
              <h3 className="text-white font-semibold">{t}</h3>
              <p className="text-sm text-wr-sub mt-1.5 leading-relaxed">{b}</p>
            </div>
          ))}
        </div>
        <p className="text-sm text-wr-muted mt-5 max-w-3xl">Overdue commitments resurface, decisions come back for review on schedule, and stale financials get flagged — so the loop never quietly breaks.</p>
      </Section>

      {/* CAPABILITY DEPTH */}
      <Section id="capabilities" kicker="Full capability depth" title="Everything Sarge delivers today.">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 border-t border-l border-wr-border">
          {capabilities.map((l, idx) => {
            const Icon = l.icon;
            return (
              <div key={l.title} data-testid={`cap-${idx}`} className="border-r border-b border-wr-border p-6 bg-wr-surface hover:bg-wr-surface2 transition-colors">
                <Icon className="text-wr-red mb-4" size={22} strokeWidth={1.6} />
                <h3 className="wr-heading font-semibold text-white text-base">{l.title}</h3>
                <p className="text-sm text-wr-sub mt-2 leading-relaxed">{l.body}</p>
              </div>
            );
          })}
        </div>
      </Section>

      {/* WHY DIFFERENT */}
      <Section id="different" kicker="Why it's different" title="Sarge keeps the operating history — not just the conversation.">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 max-w-4xl">
          {differentiators.map(([t, b], i) => (
            <div key={t} className="border border-wr-border bg-wr-surface p-5" data-testid={`diff-${i}`}>
              <h3 className="text-white font-semibold">{t}</h3>
              <p className="text-sm text-wr-sub mt-1.5 leading-relaxed">{b}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* TRUST */}
      <Section id="trust" kicker="Trust & control" title="Built so you stay in command.">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-4xl">
          {trust.map((t, i) => (
            <div key={i} className="flex items-start gap-3" data-testid={`trust-${i}`}>
              <ShieldCheck size={16} className="text-wr-red mt-0.5 shrink-0" />
              <p className="text-sm text-wr-sub leading-relaxed">{t}</p>
            </div>
          ))}
        </div>
        <p className="text-xs text-wr-muted mt-6 max-w-3xl leading-relaxed">
          Sarge is an AI and can be wrong. It is not legal, tax, investment or emergency advice, and it does not
          replace accountable human judgment. Our{" "}
          <Link to="/legal/terms" className="underline hover:text-white">Terms</Link>,{" "}
          <Link to="/legal/privacy" className="underline hover:text-white">Privacy</Link> and{" "}
          <Link to="/legal/ai_use" className="underline hover:text-white">AI Use</Link> disclosures apply.
        </p>
      </Section>

      {/* WHO FOR */}
      <Section id="who" kicker="Who it's for" title="For founders who intend to operate.">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl">
          <div className="border border-wr-border bg-wr-surface p-6" data-testid="who-for">
            <p className="wr-label !text-emerald-400">A fit if you…</p>
            <ul className="mt-3 space-y-2">
              {["Run a real business with active revenue, commitments and decisions","Have limited capacity and can't afford dropped balls","Will use a daily operating cadence, not just read tips"].map((x) => (
                <li key={x} className="flex items-start gap-2 text-sm text-wr-sub"><Check size={15} className="text-emerald-400 mt-0.5 shrink-0" /> {x}</li>
              ))}
            </ul>
          </div>
          <div className="border border-wr-border bg-wr-surface p-6" data-testid="who-not">
            <p className="wr-label !text-wr-muted">Not the right fit for…</p>
            <ul className="mt-3 space-y-2">
              {["Legal, tax, investment or emergency advice","An autonomous executive that runs the company for you","People looking only for ideas they won't act on"].map((x) => (
                <li key={x} className="flex items-start gap-2 text-sm text-wr-muted"><span className="text-wr-red mt-0.5">✕</span> {x}</li>
              ))}
            </ul>
          </div>
        </div>
      </Section>

      {/* FOUNDING BETA / PRICING */}
      <Section id="founding-beta" kicker="Founding Beta" title="The first 10 WarRooms. Start yours now.">
        <div className="border border-wr-red/40 bg-wr-redMuted p-5 sm:p-6 mb-8" data-testid="canonical-offer">
          <p className="text-sm sm:text-base text-zinc-200 leading-relaxed">
            <span className="text-white font-semibold">Founding Beta — $149/month or $1,490/year.</span>{" "}
            Limited to <span className="text-white font-semibold">10 founding WarRooms</span>. The{" "}
            <span className="text-white font-semibold">first five customers</span> receive guided setup and their
            first Command Brief. Includes a <span className="text-white font-semibold">14-day money-back guarantee</span>.
          </p>
          <p className="wr-mono text-[0.62rem] uppercase tracking-wider text-wr-muted mt-3">
            Guided setup is for the first five customers · the Founding Beta itself is limited to 10 WarRooms
          </p>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
          <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="border border-wr-border bg-wr-surface p-7" data-testid="price-monthly">
              <p className="wr-label !text-wr-red">Monthly</p>
              <p className="wr-heading font-black text-white text-4xl mt-3">$149<span className="text-lg text-wr-muted font-semibold"> / month</span></p>
              <p className="text-sm text-wr-sub mt-2">Month-to-month during your founding term.</p>
              {canBuy && <div className="mt-4"><BuyButton plan="sarge_founding_monthly" testid="buy-monthly">Start — Monthly</BuyButton></div>}
            </div>
            <div className="border border-wr-red/40 bg-wr-redMuted p-7 relative" data-testid="price-annual">
              <span className="absolute top-3 right-3 wr-mono text-[0.55rem] uppercase text-wr-red border border-wr-red/40 px-2 py-0.5">Best value</span>
              <p className="wr-label !text-wr-red">Annual</p>
              <p className="wr-heading font-black text-white text-4xl mt-3">$1,490<span className="text-lg text-wr-muted font-semibold"> / year</span></p>
              <p className="text-sm text-wr-sub mt-2">Paid upfront — save $298 a year (two months free).</p>
              {canBuy && <div className="mt-4"><BuyButton plan="sarge_founding_annual" testid="buy-annual">Start — Annual</BuyButton></div>}
            </div>
          </div>
          <div className="border border-wr-border bg-wr-surface p-7">
            <p className="wr-label">Every Founding WarRoom includes</p>
            <ul className="mt-4 space-y-2.5">
              {["One founder, one workspace","500 model requests / month","Instant setup after secure Stripe checkout","14-day money-back guarantee on your first payment","Initial pricing locked for the first 12-month term","Direct support from the WarRoom Solutions team"].map((p) => (
                <li key={p} className="flex items-start gap-2.5 text-sm text-wr-sub"><Check size={15} className="text-wr-red mt-0.5 shrink-0" /> {p}</li>
              ))}
            </ul>
            <div className="mt-6">
              {canBuy ? <BuyButton plan="sarge_founding_monthly" testid="pricing-buy-monthly">Start Founding Beta</BuyButton>
                : <RequestButton testid="pricing-request-beta" size="lg" />}
            </div>
            <div className="mt-3">
              <WalkthroughButton testid="pricing-walkthrough" />
            </div>
            {soldOut && <p className="wr-mono text-[0.62rem] text-wr-red mt-3" data-testid="sold-out">Founding Beta is full — request the waitlist.</p>}
            {billing && !billing.configured && <p className="wr-mono text-[0.62rem] text-amber-400 mt-3" data-testid="payments-configuring">Payments are being configured — join the founding beta and we'll be in touch.</p>}
          </div>
        </div>
        <p className="text-xs text-wr-muted mt-6 max-w-3xl leading-relaxed" data-testid="pricing-note">
          Payments are processed securely by Stripe — we never see your card details. 14-day money-back
          guarantee on your first payment; after that, cancel anytime and access continues through the end
          of your paid billing term (no prorated refunds except where required by law). Our Terms of Service,
          Privacy Policy, and AI Use Disclosure apply.
        </p>
      </Section>

      {/* WHAT HAPPENS AFTER YOU JOIN */}
      <Section id="after-join" kicker="What happens after you join" title="From checkout to your first command — step by step.">
        <ol className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-4xl">
          {[
            ["Choose your plan & pay", "Choose monthly or annual and complete secure checkout."],
            ["Create your account & intake", "Create the account and complete founder intake so Sarge learns how your business works."],
            ["First five: guided setup", "For the first five customers, receive guided setup and the first Command Brief."],
            ["Run the operating cadence", "Use Sarge's Daily Command Cycle and weekly review to keep priorities, commitments, risks and decisions connected."],
          ].map(([t, b], i) => (
            <li key={t} className="border-l-2 border-wr-red/40 pl-4" data-testid={`afterjoin-${i}`}>
              <p className="wr-mono text-[0.62rem] text-wr-muted">STEP {i + 1}</p>
              <h3 className="text-white font-semibold mt-1">{t}</h3>
              <p className="text-sm text-wr-sub mt-1 leading-relaxed">{b}</p>
            </li>
          ))}
        </ol>
      </Section>

      {/* PRODUCT PROOF */}
      <Section id="proof" kicker="Product proof" title="Real screens from inside a founder's WarRoom.">
        <p className="text-sm text-wr-sub max-w-3xl leading-relaxed">
          Genuine views from a demo WarRoom with sanitized sample data — not mockups.
        </p>
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
          {[
            [shotToday, "Today · Daily Command Cycle", "The day's situation, the one recommended move and why — accept, modify or reject."],
            [shotCommand, "Command Board", "Priorities, projects, commitments, risks and decisions in one operating picture."],
            [shotReview, "Weekly Operating Review", "Promised vs delivered, decisions due, risks and cash — with what Sarge learned."],
          ].map(([src, t, b], i) => (
            <figure key={t} className="border border-wr-border bg-wr-surface overflow-hidden" data-testid={`proof-${i}`}>
              <img src={src} alt={`${t} — WarRoom AI Sarge product screenshot`} loading="lazy"
                className="w-full block border-b border-wr-border" />
              <figcaption className="p-4">
                <h3 className="text-white font-semibold text-sm">{t}</h3>
                <p className="text-xs text-wr-sub mt-1 leading-relaxed">{b}</p>
              </figcaption>
            </figure>
          ))}
        </div>
      </Section>

      {/* FAQ */}
      <Section id="faq" kicker="Questions" title="Straight answers.">
        <Accordion type="single" collapsible className="max-w-3xl" data-testid="faq">
          {faqs.map(([q, a], i) => (
            <AccordionItem key={i} value={`faq-${i}`} className="border-wr-border" data-testid={`faq-item-${i}`}>
              <AccordionTrigger className="text-left text-white hover:no-underline text-sm sm:text-base">{q}</AccordionTrigger>
              <AccordionContent className="text-sm text-wr-sub leading-relaxed">{a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Section>

      {/* CLOSER */}
      <section className="border-t border-wr-border bg-black/40">
        <div className="max-w-7xl mx-auto w-full px-4 sm:px-8 py-16 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div>
            <h2 className="wr-heading font-bold text-white text-2xl sm:text-3xl">Open a WarRoom.</h2>
            <p className="text-sm text-wr-sub mt-2 max-w-xl">Founding Beta is limited to the first 10 WarRooms. Start yours in minutes with secure Stripe checkout.</p>
            <Link to="/press/sarge-founding-beta" className="wr-label hover:text-white inline-flex items-center gap-2 mt-4" data-testid="press-link"><Newspaper size={13} /> Read the announcement</Link>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 shrink-0">
            <PrimaryCTA testid="closer-request-beta" size="lg" />
            <WalkthroughButton testid="closer-walkthrough" size="lg" />
          </div>
        </div>
      </section>

      <Footer brief />

      <Dialog open={!!buyPlan} onOpenChange={(o) => !o && setBuyPlan(null)}>
        <DialogContent className="bg-wr-surface border-wr-border rounded-none max-w-md">
          <DialogHeader>
            <DialogTitle className="wr-heading text-white">Start your Founding WarRoom</DialogTitle>
            <DialogDescription className="text-wr-sub">
              {buyPlan === "sarge_founding_annual" ? "Annual — $1,490 / year paid upfront." : "Monthly — $149 / month."} You'll complete payment securely on Stripe, then set up your account.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={startCheckout} className="space-y-3" data-testid="buy-form">
            <div>
              <label className="wr-label">Your email</label>
              <Input required type="email" data-testid="buy-email" value={buyEmail} onChange={(e) => setBuyEmail(e.target.value)}
                className="mt-1.5 bg-wr-bg border-wr-border rounded-none focus-visible:ring-0" placeholder="you@company.com" />
            </div>
            <Button type="submit" disabled={buyBusy} data-testid="buy-continue"
              className="w-full rounded-none bg-wr-red hover:bg-wr-redHover text-white wr-mono uppercase tracking-wider h-11">
              {buyBusy ? "Redirecting to Stripe…" : "Continue to secure checkout"}
            </Button>
            <p className="text-xs text-wr-muted">Payment is processed by Stripe. We never see your card details. 14-day money-back guarantee on your first payment; cancel anytime after that with access through the end of your paid term.</p>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
